import { toast } from '@/components/toast';
import { projectsBucket } from '@/environment';
import { useGetPresentersList } from '@/hooks';
import { DRAFT_TEXT_PLACEHOLDER } from '@/hooks/constants';
import { useAllVoices } from '@/hooks/useAllVoices';
import { ProjectInitialData } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useGetPolotnoInitialData';
import {
    DEFAULT_FEMALE_VOICE,
    DEFAULT_MALE_VOICE,
    DEFAULT_VIDEO_NAME,
    DEFAULT_VOICE,
    languageToVoicesMap as languageToVoicesMapState,
} from '@/state/presenters';
import { walkthroughPresenterPosterFolder, walkthroughPrompt } from '@/state/walkthrough/walkthrough';
import { Emotion } from '@/utils/types';
import { unescapeSsmlText } from '@d-id/ssml';
import {
    ImageType,
    isClipsPresenter,
    isTalksPresenter,
    IVoice,
    Presenter,
    Providers,
    ScenePresenter,
    SceneTalksPresenter,
} from '@d-id/types';
import { useRecoilValue } from 'recoil';

const getLanguage = (initialData: ProjectInitialData): string | null => {
    return initialData.script?.provider?.voice_language || null;
};

const getVoiceId = (initialData: ProjectInitialData): string | null => {
    return initialData.script?.provider?.voice_id || null;
};

const getRate = (initialData: ProjectInitialData): string => {
    return initialData.script?.provider?.voice_config?.rate ?? '1';
};

const getText = (initialData: ProjectInitialData): string => {
    // todo - we are using it DRAFT_TEXT_PLACEHOLDER because of backend validation - text should be not required, then it can be removed
    const script = initialData.script?.input === DRAFT_TEXT_PLACEHOLDER ? '' : initialData.script?.input || '';

    return unescapeSsmlText(script);
};

const getDriver = (presenter?: ScenePresenter): string | undefined => {
    return presenter
        ? isClipsPresenter(presenter)
            ? presenter.driver_id
            : isTalksPresenter(presenter)
              ? presenter.driver_url
              : undefined
        : undefined;
};

const getPresenterId = (
    initialData: ProjectInitialData,
    presenters: Presenter[],
    dreamPresenters: Presenter[],
    initialAvatar?: ScenePresenter
): string | null => {
    const presenter = initialAvatar || initialData.presenter;

    // this works only for clips
    if (presenter && isClipsPresenter(presenter) && (presenter.presenter_id || presenter.id)) {
        const presenterId = presenter.presenter_id || presenter.id;
        const draftPresenterId = presenters?.find(({ id }) => id === presenterId)?.id;

        if (draftPresenterId) {
            return draftPresenterId;
        }
    }

    // this works only for talks
    else if (presenter && isTalksPresenter(presenter) && presenter?.source_url) {
        const talksPresenter = presenter as SceneTalksPresenter;
        const presenterUrl = talksPresenter.source_url.replace(/-fixed.png$/, '.png');

        const draftPresenterId = [...(presenters || []), ...dreamPresenters].find(({ url }) => url === presenterUrl)?.id;

        if (draftPresenterId) {
            return draftPresenterId;
        } else {
            toast.error({ title: 'Avatar not found.', message: 'Please choose a different Avatar' });
        }
    }

    // for projects where we don't save presenter info
    else if (presenter) {
        const childrenPresenter = initialData.rawData?.pages?.[0]?.children?.[0]?.id;
        const draftPresenterId = presenters?.find(({ id }) => id === childrenPresenter)?.id;

        if (draftPresenterId) {
            return draftPresenterId;
        }
    }

    return null;
};

const getAudio = (initialData: ProjectInitialData) => {
    if (!initialData.audio || !initialData.audio?.duration || !initialData.audio?.audio_url) return null;

    const { pathname: audioPath } = new URL(initialData.audio.audio_url);

    return {
        data: `${projectsBucket}${audioPath}`,
        duration: initialData.audio.duration,
        listenUrl: initialData.audio.audio_url,
        name: 'audio.wav',
    };
};

const getVoice = (language: string | null, voiceId: string | null, languageToVoicesMap: Record<string, IVoice[]>, voices: IVoice[]) => {
    if (!language) return null;

    let voice = languageToVoicesMap[language] && languageToVoicesMap[language][0];

    if (voiceId) {
        if (language === 'multilingual') {
            const multilingualVoice = voices.find(v => v.id === voiceId);
            voice = multilingualVoice ? { ...multilingualVoice, language: 'multilingual' } : voice;
        } else {
            voice = languageToVoicesMap[language]?.find(({ id }) => id === voiceId) ?? voice;
        }
    }

    return {
        voice,
        isSelectedDefault: false,
    };
};

const getEmotion = (initialData: ProjectInitialData): Emotion | null => {
    const presenter = initialData.presenter;
    const emotion = presenter && isTalksPresenter(presenter) && presenter.config?.driver_expressions?.expressions?.[0]?.expression;

    return emotion || null;
};

export interface NormalizedProjectData {
    language: string | null;
    rate: string;
    text: string;
    driver: string | undefined;
    presenterId: string | null;
    audio: {
        data: string;
        duration: number;
        listenUrl: string;
        name: string;
    } | null;
    voice: {
        voice: IVoice | null;
        isSelectedDefault: boolean;
    } | null;
    emotion: Emotion | null;
    videoName: string;
    avatar: ScenePresenter | undefined;
}

export const useNormalizePolotnoInitialData = () => {
    const languageToVoicesMap = useRecoilValue(languageToVoicesMapState);
    const { data: voices = [] } = useAllVoices();
    const { data: presenters = [] } = useGetPresentersList();
    const { data: dreamPresenters = [] } = useGetPresentersList({ presenterType: ImageType.Dreams });

    const normalizeInitialData = async (
        initialData: ProjectInitialData | null,
        initialAvatar?: ScenePresenter
    ): Promise<NormalizedProjectData | null> => {
        if (!initialData) return null;

        const voiceId = getVoiceId(initialData);

        const language = getLanguage(initialData);
        const rate = getRate(initialData);
        const text = getText(initialData);
        const driver = getDriver(initialAvatar || initialData.presenter);
        const presenterId = getPresenterId(initialData, presenters, dreamPresenters, initialAvatar);
        const audio = getAudio(initialData);
        const voice = getVoice(language, voiceId, languageToVoicesMap, voices);
        const emotion = getEmotion(initialData);
        const videoName = initialData.videoName || DEFAULT_VIDEO_NAME;
        const avatar = initialAvatar;

        return {
            language,
            rate,
            text,
            driver,
            presenterId,
            audio,
            voice,
            emotion,
            videoName,
            avatar,
        };
    };

    const normalizeInitialAvatar = (initialAvatar: ScenePresenter) => {
        const language = DEFAULT_VOICE.language;

        const voices = Object.values(languageToVoicesMap).flat();
        let defaultPresenterVoice =
            (initialAvatar?.voice_id && voices.find(voice => voice.id === initialAvatar.voice_id)) ||
            (initialAvatar?.voice && voices.find(voice => voice.id === initialAvatar.voice?.voice_id));

        if (defaultPresenterVoice && defaultPresenterVoice.provider === Providers.Elevenlabs) {
            defaultPresenterVoice = { ...defaultPresenterVoice, language: 'multilingual' };
        } else if (initialAvatar?.gender === 'male' || initialAvatar?.gender === 'female') {
            defaultPresenterVoice = voices.find(
                voice => voice.id === (initialAvatar.gender === 'male' ? DEFAULT_MALE_VOICE : DEFAULT_FEMALE_VOICE)
            );
        }

        const defaultVoice = voices.find(voice => voice.language === language) || voices[0] || null;
        const voice = defaultPresenterVoice || defaultVoice;

        return { voice, isSelectedDefault: true, language };
    };

    const normalizeWalkthroughData = () => {
        const avatar = presenters.find(({ poster }) => poster.includes(walkthroughPresenterPosterFolder));

        return avatar ? { text: walkthroughPrompt, avatar } : { text: walkthroughPrompt };
    };

    return { normalizeInitialData, normalizeInitialAvatar, normalizeWalkthroughData };
};
